import "reflect-metadata";
import _ from "lodash";
import { RevisionItem } from "./RevisionItem";
import { ResourceItem } from "./ResourceItem";
import Decimal from "decimal.js";

type ResourceConstructor = new (data: any) => ResourceItem;

interface ApiFieldOptions {
  inputName?: string;
  inputTransform?: (value: any) => any;

  outputName?: string;
  outputTransform?: (value: any) => any;

  readOnly?: boolean;

  // undefined means default, null means skip
  getDiff?: ((value: any, other: any) => any) | null;
}

const PROPERTY_OPTIONS_KEY = Symbol();
const CLASS_OPTIONS_KEY = Symbol();

export const apiField =
  (options: ApiFieldOptions = {}) =>
  (target: any, propertyKey: string) => {
    Reflect.defineMetadata(PROPERTY_OPTIONS_KEY, options, target, propertyKey);

    // update fields on the class
    let apiOptionsFields = Reflect.getOwnMetadata(CLASS_OPTIONS_KEY, target);

    if (!apiOptionsFields) {
      // merge with inherited fields, if available
      apiOptionsFields = Reflect.hasMetadata(CLASS_OPTIONS_KEY, target)
        ? Reflect.getMetadata(CLASS_OPTIONS_KEY, target).slice(0)
        : [];

      // define own fields on the target
      Reflect.defineMetadata(CLASS_OPTIONS_KEY, apiOptionsFields, target);
    }

    // record this field
    apiOptionsFields.push(propertyKey);
  };

function iterateObjectFieldOptions(
  obj: any,
  callback: (propKey: string, options: ApiFieldOptions) => any,
): void {
  const propKeys: string[] | undefined = Reflect.getMetadata(
    CLASS_OPTIONS_KEY,
    obj,
  );

  if (propKeys)
    propKeys.forEach((propKey) => {
      const options: ApiFieldOptions = Reflect.getMetadata(
        PROPERTY_OPTIONS_KEY,
        obj,
        propKey,
      );
      callback(propKey, options);
    });
}

/**
 * Applies and transforms all properties of the object according to the metadata options set with the apiField decorator
 */
export function constructFromApiData(obj: any, inputData?: any): void {
  iterateObjectFieldOptions(obj, (propKey, options) => {
    const inputValue = inputData[options.inputName || propKey];
    if (inputValue != undefined)
      obj[propKey] = options.inputTransform
        ? options.inputTransform(inputValue)
        : inputValue;
  });
}

export function getApiPostData(obj: any) {
  const result: { [key: string]: any } = {};

  iterateObjectFieldOptions(obj, (propKey, options) => {
    const fieldValue = obj[propKey];
    if (!options.readOnly && fieldValue != null) {
      const outputValue = options.outputTransform
        ? options.outputTransform(fieldValue)
        : fieldValue;

      result[options.outputName || propKey] = outputValue;
    }
  });

  return result;
}

const defaultFieldGetDiff = (a: any, b: any) => (a !== b ? true : false);

export function getDiff<T extends { [key: string]: any }>(obj: T, other: T) {
  const result: any = {};

  iterateObjectFieldOptions(obj, (propKey, options) => {
    if (options.getDiff !== null) {
      const diffFunction = options.getDiff || defaultFieldGetDiff;

      const propResult = diffFunction(obj[propKey], other[propKey]);
      if (propResult) result[propKey] = propResult;
    }
  });

  if (!Object.keys(result).length) return undefined;
  return result;
}

export const itemApiField = (
  constructor: ResourceConstructor,
  options: ApiFieldOptions = {},
) =>
  apiField({
    inputTransform: (x) => new constructor(x),
    outputTransform: (x) => ({ id: x.id }),
    getDiff: (a, b) => (!a || !b ? a !== b : a.id !== b.id),
    ...options,
  });

export const itemArrayApiField = (
  constructor: ResourceConstructor,
  options: ApiFieldOptions = {},
) =>
  apiField({
    inputTransform: (list: any[]) => list.map((x) => new constructor(x)),
    outputTransform: (list: any[]) => list.map((x) => ({ id: x.id })),
    getDiff: (a, b) => !_.isEqual(a, b),
    ...options,
  });

export const revisionItemApiField = (
  options: ApiFieldOptions = {},
  constructor = RevisionItem,
) =>
  itemApiField(constructor, {
    outputTransform: (x) => (x ? { activeId: x.activeId } : null),
    ...options,
  });

export const revisionApiField = (options: ApiFieldOptions = {}) =>
  apiField({
    outputTransform: (x) => (x ? { activeId: x.activeId } : null),
    ...options,
  });

export const dateApiField = (options: ApiFieldOptions = {}) =>
  apiField({
    inputTransform: (d) => new Date(d),
    outputTransform: (d) => d.toISOString(),
    ...options,
  });

export const nestedItemArrayApiField = (
  constructor: ResourceConstructor,
  options: ApiFieldOptions = {},
) =>
  apiField({
    inputTransform: (list: any[]) => list.map((x) => new constructor(x)),
    outputTransform: (list: ResourceItem[]) => list.map((x) => x.getPostData()),
    getDiff: (a: ResourceItem[], b: ResourceItem[]) =>
      _.xorWith(a, b, (x, y) => x.id != y.id).map((o) => o.id),
    ...options,
  });

export const decimalApiField = (options: ApiFieldOptions = {}) =>
  apiField({
    inputTransform: (str: string) => (str ? new Decimal(str) : null),
    outputTransform: (dec: Decimal) => (dec ? dec.toString() : null),
    getDiff: (a: Decimal | null, b: Decimal | null) => {
      if (a === b) return false;
      if (b === null || a === null) return true;
      return !a.equals(b);
    },
    ...options,
  });
